/**
 * Utilidades de color para los hex de marca que llegan de Salesforce.
 *
 * Los colores de plan (`Product2.StockKeepingUnit`) van de #03D1E0 a #333333:
 * son datos, no tokens de diseño. Nunca hay que asumir un color de texto fijo
 * encima ni una luminosidad determinada.
 */

/** Luminancia relativa WCAG 2.x de un color hexadecimal. */
const luminancia = (hexColor: string): number => {
  const hex = hexColor.replace('#', '');
  const canal = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const r = canal(parseInt(hex.substring(0, 2), 16) / 255);
  const g = canal(parseInt(hex.substring(2, 4), 16) / 255);
  const b = canal(parseInt(hex.substring(4, 6), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Devuelve el color de texto (negro o blanco) que mejor contrasta sobre el
 * fondo dado.
 *
 * Se comparan los colores que realmente se van a pintar, no negro/blanco
 * teóricos: usar #0f172a y medir contra #000000 sobreestima el contraste y
 * deja pasar combinaciones que fallan AA (p. ej. texto oscuro sobre el
 * magenta #B24BC5).
 */
export const textoSobre = (hexColor: string): string => {
  const contraste = (a: number, b: number) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  const fondo = luminancia(hexColor);

  const candidatos = ['#000000', '#ffffff'];
  return candidatos.reduce((mejor, actual) =>
    contraste(fondo, luminancia(actual)) > contraste(fondo, luminancia(mejor)) ? actual : mejor,
  );
};

/** Convierte un hexadecimal a rgba con la opacidad dada. */
export const hexToRgba = (hexColor: string, alpha = 0.1): string => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
